// 1:1 port from age-of-mcp v3 canteen — an open-air dining terrace read from
// above: a plaster kitchen hall across the back under a wheat-gold roof
// (ROOF_FOOD) with a stone chimney, and an uncovered terrace with two trestle
// tables flanked by benches out front, where the lunch crowd sits and eats.
// The 8 eat seats are derived from the same constants that place the tables; a
// hauler restocking the kitchen leans at the serving hatch.
import { defineModel, sitEat, leanLoad, styleV3, type Part } from "../../lib";

const {
  PLASTER, WOOD, WOOD_DK, STONE, CANVAS, STEEL, HAZARD, DOOR, FIRE, ROOF_FOOD,
  box, cyl, slab, poles, gable,
} = styleV3;

const BASE   = 0.12;
const HZ     = -0.52;          // hall centre z (the back of the plot)
const HALL_W = 1.8;
const HALL_D = 0.8;
const WALL_H = 0.78;
const FRONT  = HZ + HALL_D / 2;   // -0.12 — the hall's serving face
const HATCH_X = 0.4;           // serving hatch position on that face

// The terrace tables: two trestle sets out front, in the open.
const TABLE_X  = [-0.48, 0.5] as const;
const TABLE_Z  = 0.5;
const BENCH_DZ = 0.27;         // bench offset from its table centre

// The plaster kitchen hall, `h` tall on the foundation.
function hall(h: number): Part {
  return box(HALL_W, h, HALL_D, PLASTER, [0, BASE + h / 2, HZ]);
}

// One trestle dining set at `tx`: a table on a centre leg, two benches.
function diningSet(tx: number): Part[] {
  const out: Part[] = [
    box(0.52, 0.05, 0.3, WOOD,    [tx, BASE + 0.27, TABLE_Z]),
    box(0.1, 0.24, 0.22, WOOD_DK, [tx, BASE + 0.12, TABLE_Z]),
  ];
  for (const dz of [-BENCH_DZ, BENCH_DZ]) {
    out.push(
      box(0.5, 0.05, 0.13, WOOD,   [tx, BASE + 0.135, TABLE_Z + dz]),
      box(0.08, 0.1, 0.1, WOOD_DK, [tx, BASE + 0.05, TABLE_Z + dz]),
    );
  }
  return out;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(1.94, 1.94)];
  if (s === 1) return [slab(1.94, 1.94), hall(0.32), ...poles(1.8, 1.8, 0.7)];
  if (s === 2) return [slab(1.94, 1.94), hall(WALL_H), ...poles(1.8, 1.8, 1.3)];

  const top = BASE + WALL_H;          // 0.90 — the eave line
  const face = FRONT + 0.01;
  return [
    slab(1.94, 1.94),
    hall(WALL_H),
    // Wheat-gold gable over the hall — ridge along x. ONE part.
    gable(0.96, 1.92, 0.5, ROOF_FOOD, 0, top, 0, "x"),
    // Stone kitchen chimney with a smoke puff, back-left.
    box(0.18, 0.62, 0.18, STONE,  [-0.65, top + 0.2, HZ - 0.15]),
    box(0.16, 0.12, 0.16, CANVAS, [-0.65, top + 0.58, HZ - 0.15]),
    // Serving hatch: dark opening, counter shelf, a steaming stew pot.
    box(0.5, 0.3, 0.05, HAZARD,  [HATCH_X, BASE + 0.52, face]),
    box(0.56, 0.06, 0.16, WOOD,  [HATCH_X, BASE + 0.36, face + 0.06]),
    cyl(0.1, 0.08, 0.14, 8, STEEL, [HATCH_X, BASE + 0.49, face + 0.07]),
    cyl(0.09, 0.09, 0.03, 8, FIRE, [HATCH_X, BASE + 0.575, face + 0.07]),
    // The kitchen door.
    box(0.3, 0.55, 0.05, DOOR, [-0.5, BASE + 0.275, face]),
    // The OPEN terrace: two dining sets where the workers eat.
    ...diningSet(TABLE_X[0]),
    ...diningSet(TABLE_X[1]),
    // A water barrel at the hall corner.
    cyl(0.13, 0.12, 0.28, 8, WOOD_DK, [-0.85, BASE + 0.14, 0.12]),
  ];
}

export const canteenV3 = defineModel({
  id: "canteen-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Canteen (v3)",
  tags: ["building","canteen","low-poly","age-of-mcp","v3"],
  summary: "Low-poly canteen.",
  description: "A mess hall where workers eat, kept stocked with food.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "seat-0", position: [-0.61, 0.28, 0.23], facing: 0 },
    { id: "seat-1", position: [-0.35, 0.28, 0.23], facing: 0 },
    { id: "seat-2", position: [-0.61, 0.28, 0.77], facing: Math.PI },
    { id: "seat-3", position: [-0.35, 0.28, 0.77], facing: Math.PI },
    { id: "seat-4", position: [0.37, 0.28, 0.23], facing: 0 },
    { id: "seat-5", position: [0.63, 0.28, 0.23], facing: 0 },
    { id: "seat-6", position: [0.37, 0.28, 0.77], facing: Math.PI },
    { id: "seat-7", position: [0.63, 0.28, 0.77], facing: Math.PI },
    { id: "serve", position: [0.4, 0.12, 0.18], facing: Math.PI, description: "The serving window." },
  ],
  interactions: [
    { id: "dine", label: "Eating", station: "seat-0", variants: ["complete"], ...sitEat() },
    { id: "load", label: "Loading supplies", station: "serve", variants: ["complete"], ...leanLoad() },
  ],
});
